import { toCssVariables } from "../tokens/palette";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "../tokens/fonts";

// Destructive row actions: error-coloured buttons, Delete/Disapprove by label, and tray entries.
const DANGER_ROW_ACTION_HOVER = [
  ".MuiDataGrid-cell .MuiIconButton-colorError:hover",
  '.MuiDataGrid-cell .MuiIconButton-root[aria-label^="Delete" i]:hover',
  '.MuiDataGrid-cell .MuiIconButton-root[aria-label^="Disapprove" i]:hover',
  ".row-action-button--danger.MuiIconButton-root:hover",
].join(", ");

export default function globals({ colors, tokens, mode }) {
  const { info, text } = colors;

  return {
    // Tokens as CSS variables (var(--accent), var(--surface-2), ...) for the app's plain CSS files.
    ":root": {
      ...toCssVariables(tokens),
      "--font-display": FONT_DISPLAY,
      "--font-body": FONT_BODY,
      "--font-mono": FONT_MONO,
      "--radius-lg": "14px",
      "--radius": "10px",
      "--radius-sm": "7px",
      "--radius-pill": "999px",
      // Scrollbar thumb colours. A region on a different background (the ink side menu) sets its
      // own; the variables inherit, so everything that scrolls inside it follows.
      "--scrollbar-thumb": tokens["border-strong"],
      "--scrollbar-thumb-hover": tokens["field-border"],
      colorScheme: mode,
    },
    // Slim scrollbars everywhere: the page, tables, data grids, menus and dialogs. A rounded thumb
    // on a clear track, thickening a little under the pointer. Chromium and Safari draw these
    // pseudo-elements; Firefox only supports the standard properties, so it gets those instead.
    // (Chromium ignores the pseudo-elements on any element given scrollbar-width or -color, hence
    // the @supports guard.)
    "::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "::-webkit-scrollbar-track, ::-webkit-scrollbar-corner": {
      background: "transparent",
    },
    "::-webkit-scrollbar-thumb": {
      minHeight: "40px",
      minWidth: "40px",
      border: "3px solid transparent",
      borderRadius: "var(--radius-pill)",
      backgroundColor: "var(--scrollbar-thumb)",
      backgroundClip: "padding-box",
    },
    "::-webkit-scrollbar-thumb:hover": {
      borderWidth: "2px",
      backgroundColor: "var(--scrollbar-thumb-hover)",
    },
    "::-webkit-scrollbar-button": {
      display: "none",
    },
    // Row actions (icon buttons in grid cells and the row-actions tray): small grey line icons
    // that take the accent under the pointer, so they don't compete with the row's data.
    // Destructive ones (Delete, Disapprove, error-coloured) turn red only on hover.
    ".MuiDataGrid-cell .MuiIconButton-root, .row-action-button.MuiIconButton-root": {
      padding: "6px",
      borderRadius: "var(--radius-sm)",
      color: tokens["text-3"],
      transition: "color 120ms ease, background-color 120ms ease",
      "& .MuiSvgIcon-root": {
        fontSize: "18px !important",
      },
      "&:hover": {
        color: tokens.accent,
        backgroundColor: tokens["accent-soft"],
      },
      "&.Mui-disabled": {
        color: tokens["text-3"],
        opacity: 0.5,
      },
    },
    [DANGER_ROW_ACTION_HOVER]: {
      color: tokens.error,
      backgroundColor: tokens["error-soft"],
    },
    "@supports not selector(::-webkit-scrollbar)": {
      "*": {
        scrollbarWidth: "thin",
        scrollbarColor: "var(--scrollbar-thumb) transparent",
      },
    },
    html: {
      scrollBehavior: "smooth",
    },
    body: {
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    },
    "::selection": {
      background: tokens["accent-soft-2"],
    },
    "*, *::before, *::after": {
      margin: 0,
      padding: 0,
    },
    "a, a:link, a:visited": {
      color: info.main,
      textDecoration: "none !important",
    },
    "a.link, .link, a.link:link, .link:link, a.link:visited, .link:visited": {
      color: `${text.primary} !important`,
      transition: "color 150ms ease-in !important",
    },
    "a.link:hover, .link:hover, a.link:focus, .link:focus": {
      color: `${info.main} !important`,
    },
  };
}
